import {
  Fragment,
  ReactElement,
  useCallback, useEffect, useMemo, useRef, useState
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { debounce } from 'lodash'
import cn from 'classnames'
import { Contact } from 'common/components/Contact'
import { ActionState } from 'common/components/Contact/types'
import { EmptySearchState } from 'common/components/EmptySearchState_V2'
import { UsersActionsSkeleton } from 'common/components/SkeletonLoader/components/UsersActionsSkeleton'
import { EmptyContainerView } from 'common/components/UsersActionsContainer/components/EmptyContainerView'
import { UserSection } from 'common/components/UsersActionsContainer/types'
import {
  calculateSectionUsersLength, getGlobalSearchQueryParams, isSearchStringValid,
  isSectionsEmpty
} from 'common/components/UsersActionsContainer/utils'
import { SEARCH_DEBOUNCE_DELAY } from 'common/constants'
import { useProfileNavigation } from 'common/hooks/useProfileNavigation'
import { Typography, TypographyVariants } from 'common/typography'
import { formatUserName } from 'common/utils/formatUserName'
import { searchInString } from 'common/utils/searchInString'
import { actions as actionsHome } from 'features/Home/actions'
import { selectGlobalSearchUsers } from 'features/Home/selectors'
import { Modal } from 'features/Modal_V2'
import { getGlobalSearchUsers } from 'features/MyProfile/actions'
import { selectContactUids, selectTrustedUids, selectTrustLevels } from 'features/MyProfile/selectors'
import { ContactsType, ProfileType, UserBriefInfo } from 'features/MyProfile/types'
import { SearchInput } from 'features/SearchInput'
import { selectToastMessages } from 'features/Translations/selectors'
import styles from './styles.module.sass'

interface IUsersActionsContainer {
  sections: UserSection[]
  isOpen?: boolean
  navigateOnClick?: boolean
  onClose?: () => void
  onSubmit: (uid: string, userData: ProfileType) => void
  getUserInfo?: (user: ProfileType, isSecondLevelTrusted: boolean) => string
  getButtonTitle: (uid: string) => string
  getActionState: (uid: string) => ActionState
  containerTitle: string
  emptySearchTitle: string
  emptySearchSubTitle: string
  searchPlaceholder: string
  buttonType?: string
  isLayoutView?: boolean
  emptyContainerIcon?: ReactElement
  emptyContainerTitle?: string
  emptyContainerDescription?: string
  imgWidth?: number
  startSearchIfValid?: boolean
}

export const UsersActionsContainer = ({
  sections,
  isOpen,
  navigateOnClick,
  onClose,
  onSubmit,
  getButtonTitle,
  getActionState,
  getUserInfo,
  containerTitle,
  emptySearchTitle,
  emptySearchSubTitle,
  searchPlaceholder,
  buttonType,
  isLayoutView = false,
  emptyContainerIcon,
  emptyContainerTitle,
  emptyContainerDescription,
  imgWidth = 32,
  startSearchIfValid = false
}: IUsersActionsContainer) => {
  const dispatch = useDispatch()
  const location = useLocation()
  const globalSearchUsers = useSelector(selectGlobalSearchUsers)
  const trustedUids = useSelector(selectTrustedUids)
  const allContactsUids = useSelector(selectContactUids)
  const trustLevels = useSelector(selectTrustLevels)
  const toastMessages = useSelector(selectToastMessages)
  const { navigateToProfile } = useProfileNavigation()
  const [searchString, setSearchString] = useState('')
  const [loadingGlobalSearchUsers, setLoadingGlobalSearchUsers] = useState(false)
  const { excludeUids, limit, showGlobalSearchData } = getGlobalSearchQueryParams(sections, searchString)
  const isEmpty = isSectionsEmpty(sections)
  const isLoading = loadingGlobalSearchUsers ?? false
  const activeRequestsRef = useRef(0)

  const filteredSections = useMemo(() => {
    if (startSearchIfValid && !isSearchStringValid(searchString)) {
      return sections
    }
    const updatedSections = sections?.map((section) => {
      if (section.enableGlobalSearch && isSearchStringValid(searchString)) {
        return {
          ...section,
          users: [...globalSearchUsers]
        }
      }
      return section
    })

    return updatedSections?.map((section) => ({
      ...section,
      users: section.users?.filter((user) => searchInString(searchString, user.displayName))
    }))
  }, [sections, searchString, globalSearchUsers])

  const filteredUsersLength = calculateSectionUsersLength(filteredSections)
  const isModalEmpty =
    !filteredUsersLength &&
    !isLoading && !globalSearchUsers.length &&
    !isSearchStringValid(searchString) &&
    !isLayoutView

  const debouncedSearch = useCallback(debounce(async (query: string) => {
    activeRequestsRef.current += 1
    const users = await getGlobalSearchUsers(query, dispatch, toastMessages, excludeUids, limit)
    if (showGlobalSearchData) {
      dispatch(actionsHome.setGlobalSearchUsers(users))
    }
    activeRequestsRef.current -= 1
    if (activeRequestsRef.current === 0) {
      setLoadingGlobalSearchUsers(false)
    }
  }, SEARCH_DEBOUNCE_DELAY), [showGlobalSearchData, toastMessages, excludeUids, limit])

  const resetGlobalSearchData = () => {
    dispatch(actionsHome.setGlobalSearchUsers([]))
    setLoadingGlobalSearchUsers(false)
  }

  const onChange = (searchString: string) => {
    setSearchString(searchString)
    if (!isSearchStringValid(searchString)) {
      resetGlobalSearchData()
    }
  }

  useEffect(() => {
    if (showGlobalSearchData) {
      setLoadingGlobalSearchUsers(true)
      debouncedSearch(searchString)
    }
    return () => {
      debouncedSearch.cancel()
    }
  }, [searchString, showGlobalSearchData])

  const renderItems = (items: ContactsType[] | UserBriefInfo[] = []) => (
    items.map((contact: any) => {
      const {
        photoURL, uid, photo, username
      } = contact
      const isSecondLevelTrusted = trustLevels[uid] === 2

      return (
        <Contact
          key={uid}
          isTrusted={trustedUids.includes(uid)}
          isContact={allContactsUids.includes(uid)}
          searchString={searchString}
          name={formatUserName(contact)}
          photoUrl={photoURL}
          photo={photo}
          imgWidth={imgWidth}
          uid={uid}
          buttonType={buttonType}
          contactAction={() => onSubmit(uid, contact)}
          onClick={navigateOnClick ? () => navigateToProfile(location.pathname, uid, username) : undefined}
          actionState={getActionState(uid)}
          actionTitle={getButtonTitle(uid)}
          profileItemStyles={styles.userStyles}
          trustedByData={getUserInfo?.(contact, isSecondLevelTrusted)}
        />
      )
    })
  )

  const getEmptySearchState = () => {
    if (isModalEmpty) {
      return (
        <EmptyContainerView
          icon={emptyContainerIcon}
          title={emptyContainerTitle}
          description={emptyContainerDescription}
          isLayoutView={isLayoutView}
        />
      )
    }
    return (
      <EmptySearchState
        emptySearchTitle={emptySearchTitle}
        emptySearchSubTitle={emptySearchSubTitle}
        emptySearchStateContent={cn(styles.emptySearchStatesActions, isLayoutView && styles.layoutViewContent)}
        isBackgroundWhite
      />
    )
  }

  const getContentData = () => {
    if (!filteredUsersLength && !loadingGlobalSearchUsers) return getEmptySearchState()
    return (
      <div className={cn(styles.modalContent, isLayoutView && styles.layoutViewContent)}>
        {isLoading
          ? <UsersActionsSkeleton />
          : filteredSections.map((section, index) => {
            if (!section.users?.length) return null
            return (
              <Fragment key={index}>
                {section.title && (
                  <Typography variant={TypographyVariants.Desktop_UI_S_Medium} tag="span" className={styles.sectionTitle}>
                    {section.title}
                  </Typography>
                )}
                <div className={styles.userList}> {renderItems(section.users)} </div>
              </Fragment>
            )
          })}
      </div>
    )
  }

  const getLayoutViewContent = () => {
    if (isEmpty) {
      return (
        <EmptyContainerView
          icon={emptyContainerIcon}
          title={emptyContainerTitle}
          description={emptyContainerDescription}
          isLayoutView={isLayoutView}
        />
      )
    }
    return (
      <>
        <span className={styles.containerTitle}>
          <Typography variant={TypographyVariants.Desktop_UI_L_Medium} tag="span">
            {containerTitle}
          </Typography>
          <Typography variant={TypographyVariants.Desktop_UI_L_Medium} tag="span" className={styles.count}>
            {filteredUsersLength}
          </Typography>
        </span>
        <div className={styles.modalContentWrapper}>
          <SearchInput
            onChange={onChange}
            value={searchString}
            placeholder={searchPlaceholder}
          />
          {getContentData()}
        </div>
      </>
    )
  }

  const getModalContent = () => {
    return (
      <Modal
        title={containerTitle}
        isOpen={isOpen ?? false}
        onClose={onClose}
        width={446}
        containerStyles={styles.containerStyles}
        scrollableContentStyles={styles.scrollableContentStyles}
        scrollableContent={getContentData()}
      >
        <SearchInput
          onChange={onChange}
          value={searchString}
          placeholder={searchPlaceholder}
          className={styles.searchInput}
        />
      </Modal>
    )
  }

  return (
    <div className={cn(
      styles.usersActionsWrapper,
      isLayoutView && styles.layoutViewWrapper,
      isLayoutView && isEmpty && styles.emptyLayoutViewWrapper
    )}
    >
      {isLayoutView ? getLayoutViewContent() : getModalContent()}
    </div>
  )
}
